import { useMemo, useState, type ReactNode } from 'react';
import { VendorLogoBlock } from 'src/components/cart/VendorLogoBlock';
import { DropdownStyled } from 'src/components/common/DropdownStyled';
import { type DiscountBucket, type StoreOption } from './dealFilters';

const DISCOUNT_OPTIONS: { value: DiscountBucket | null; label: string }[] = [
  { value: null, label: 'All discounts' },
  { value: 20, label: '20% off or more' },
  { value: 40, label: '40% off or more' },
  { value: 60, label: '60% off or more' },
];

type Props = {
  stores: StoreOption[];
  vendor: string | null;
  discount: DiscountBucket | null;
  hasCode: boolean;
  expiringSoon: boolean;
  canClear: boolean;
  onVendorChange: (vendor: string | null) => void;
  onDiscountChange: (discount: DiscountBucket | null) => void;
  onHasCodeChange: (hasCode: boolean) => void;
  onExpiringSoonChange: (expiringSoon: boolean) => void;
  onClear: () => void;
};

const storeMenuWidth = 'w-full sm:w-[220px]';
const storePanelWidth = 'w-[min(28rem,calc(100vw-2rem))] sm:w-[220px]';
const discountMenuWidth = 'w-full sm:w-[180px]';
const discountPanelWidth = 'w-[min(28rem,calc(100vw-2rem))] sm:w-[180px]';
const chipClass = (active: boolean) =>
  `h-10 w-full rounded px-3 font-secondary text-sm font-semibold transition-all sm:w-auto ${
    active
      ? 'bg-primary text-offWhite hover:bg-primary-hover'
      : 'bg-cloudGray text-primary-dark hover:bg-cloudGray/80'
  }`;

export const DealsFilters = ({
  stores,
  vendor,
  discount,
  hasCode,
  expiringSoon,
  canClear,
  onVendorChange,
  onDiscountChange,
  onHasCodeChange,
  onExpiringSoonChange,
  onClear,
}: Props) => {
  const [storeQuery, setStoreQuery] = useState('');

  const visibleStores = useMemo(() => {
    const q = storeQuery.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((store) => store.label.toLowerCase().includes(q));
  }, [stores, storeQuery]);

  const selectedStore = stores.find((store) => store.slug === vendor);
  const storeButtonLabel = selectedStore?.label ?? 'All stores';
  const discountButtonLabel =
    DISCOUNT_OPTIONS.find((opt) => opt.value === discount)?.label ?? 'All discounts';

  return (
    <div className='flex w-full max-w-md shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-2 sm:gap-y-2 lg:justify-start'>
      <div className={`relative ${storeMenuWidth} sm:shrink-0`}>
        <DropdownStyled
          darkStyle={false}
          customHeight='h-10'
          className='w-full'
          buttonContent={
            <div className='flex min-w-0 items-center gap-1.5'>
              {selectedStore && (
                <VendorLogoBlock
                  vendor={selectedStore.slug}
                  size='small'
                  customSrc={selectedStore.imageUrl ?? undefined}
                />
              )}
              <span className='truncate font-secondary text-sm font-semibold text-primary-dark'>
                {storeButtonLabel}
              </span>
            </div>
          }
        >
          {(closeDropdown) => (
            <div className={`${storePanelWidth} rounded-lg bg-cloudGray`}>
              <div className='bg-cloudGray px-2 pt-2' onClick={(e) => e.stopPropagation()}>
                <input
                  type='text'
                  value={storeQuery}
                  onChange={(e) => setStoreQuery(e.target.value)}
                  placeholder='Search stores'
                  aria-label='Search stores'
                  className='h-10 w-full rounded-[4px] border-2 border-gray-200 bg-white px-2.5 py-1.5 font-secondary text-sm font-normal text-primary-dark transition hover:cursor-text hover:bg-cloudGray focus:border-primary focus:outline-none focus:ring-0'
                />
              </div>
              <div
                role='listbox'
                aria-label='Select store'
                className='mb-1.5 max-h-[180px] overflow-y-auto py-1'
              >
                <FilterOption
                  selected={vendor == null}
                  onSelect={() => {
                    setStoreQuery('');
                    onVendorChange(null);
                    closeDropdown();
                  }}
                >
                  All stores
                </FilterOption>
                {visibleStores.length === 0 ? (
                  <p className='px-3 py-1.5 font-secondary text-sm text-offGray'>No stores</p>
                ) : (
                  visibleStores.map((store) => (
                    <FilterOption
                      key={store.slug}
                      selected={vendor === store.slug}
                      onSelect={() => {
                        setStoreQuery('');
                        onVendorChange(store.slug);
                        closeDropdown();
                      }}
                    >
                      <img
                        src={
                          store.imageUrl ||
                          `${process.env.REACT_APP_IMG_URL}/images/favis/32/${store.slug}.png`
                        }
                        className='h-5 w-5 shrink-0 rounded-sm'
                        alt=''
                      />
                      <span className='min-w-0 truncate'>{store.label}</span>
                    </FilterOption>
                  ))
                )}
              </div>
            </div>
          )}
        </DropdownStyled>
      </div>

      <div className={`relative ${discountMenuWidth} sm:shrink-0`}>
        <DropdownStyled
          darkStyle={false}
          customHeight='h-10'
          className='w-full'
          buttonContent={
            <span className='truncate font-secondary text-sm font-semibold text-primary-dark'>
              {discountButtonLabel}
            </span>
          }
        >
          {(closeDropdown) => (
            <div
              role='listbox'
              aria-label='Select discount'
              className={`max-h-[180px] ${discountPanelWidth} overflow-y-auto rounded-lg bg-cloudGray py-1`}
            >
              {DISCOUNT_OPTIONS.map((opt) => (
                <FilterOption
                  key={opt.label}
                  selected={opt.value === discount}
                  onSelect={() => {
                    onDiscountChange(opt.value);
                    closeDropdown();
                  }}
                >
                  {opt.label}
                </FilterOption>
              ))}
            </div>
          )}
        </DropdownStyled>
      </div>

      <button
        type='button'
        aria-pressed={hasCode}
        onClick={() => onHasCodeChange(!hasCode)}
        className={chipClass(hasCode)}
      >
        Has code
      </button>

      <button
        type='button'
        aria-pressed={expiringSoon}
        onClick={() => onExpiringSoonChange(!expiringSoon)}
        className={chipClass(expiringSoon)}
      >
        Expiring soon
      </button>

      <button
        type='button'
        onClick={onClear}
        disabled={!canClear}
        className={`h-10 w-full px-1 font-secondary text-sm font-semibold text-skyBlue transition-opacity sm:w-auto sm:shrink-0 ${
          canClear ? 'hover:opacity-80' : 'cursor-default opacity-40'
        }`}
      >
        Clear filters
      </button>
    </div>
  );
};

const FilterOption = ({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) => (
  <button
    type='button'
    role='option'
    aria-selected={selected}
    className={`flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md px-2.5 py-1.5 text-left font-secondary text-sm font-semibold text-primary transition-all hover:bg-primary/10 ${
      selected ? 'bg-primary/10' : ''
    }`}
    onClick={onSelect}
  >
    {children}
  </button>
);
